import { useCallback, useEffect, useMemo } from 'react'
import { toast } from 'react-toastify'
import {
  CreatePlaybookRequest,
  FavoriteRequest,
  ListPlaybooksRequest,
  MakePrivateRequest,
  ObjectIdRequest,
  Playbook,
  PlaybookContext,
  PlaybookInputs,
  PlaybookRunParameter,
  PlaybookRunPromptParameter,
  PlaybookParameter,
  PlaybookPrompt,
  PlaybookResponse,
  PlaybooksResponse,
  RunPlaybookRequest,
  RunPlaybookResponse,
  UpdatePlaybookRequest,
  UploadFilesRequest,
  ValueSource,
} from '@trustero/trustero-api-web/lib/playbook/playbook_pb'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { useUser } from 'src/components/async/Users'
import {
  useHasVendorTprmQuestionnaire,
  useResolvedPlaybookFeatureFlags,
} from 'src/context/Gatekeeper'
import {
  ListChatsForPlaybookRequest,
  ListChatsForPlaybookResponse,
} from '@trustero/trustero-api-web/lib/chat/chat_pb'
import { ChatServicePromiseClient } from '@trustero/trustero-api-web/lib/chat/chat_grpc_web_pb'
import { ChatContextSourceType } from '@trustero/trustero-api-web/lib/chatcontext/chatcontext_pb'
import {
  FileUploadBody,
  MODEL_TYPE,
} from '@trustero/trustero-api-web/lib/common/model_pb'
import type { SelectItem } from 'src/components/Reusable/SelectDropdown/SelectDropdown.constants'
import { getBytesPayload } from 'src/Utils/File/file'
import { PlaybookServicePromiseClient } from '@trustero/trustero-api-web/lib/playbook/playbook_grpc_web_pb'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import { useSwrGrpc, callGrpc } from 'src/components/async/useSwrGrpc'
import { useAuthHeader } from 'src/context/authContext'
import { getRefererHeader } from 'src/adapter/referer'
import { useSWRConfig } from 'swr'
import log from 'loglevel'
import {
  FilterValue,
  useFilterBarValues,
} from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.context'
import { SearchableData } from 'src/Utils/search/search.types'
import { prepareSearchableData } from 'src/Utils/search/search.helpers'
import { useFilters } from 'src/Utils/filter/filter.hooks'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import { Filter, Transform } from 'src/Utils/filter/filter'
import {
  useCreateChat,
  useUpdateChat,
  useSendChatMessage,
  useNavigateToChat,
} from '../trusteroIntelligence.hooks'
import { useSetPendingPromptForChat } from '../trusteroIntelligence.context'
import type {
  Parameter,
  ParameterType,
  SelectedEvidence,
  SelectedPlaybook,
} from './playbooks.components'

export const CATEGORY_OPTIONS: SelectItem[] = [
  { name: 'Compliance', value: 'Compliance' },
  { name: 'Security', value: 'Security' },
  { name: 'Privacy', value: 'Privacy' },
  { name: 'Risk Management', value: 'Risk Management' },
  { name: 'Audit', value: 'Audit' },
  { name: 'Governance', value: 'Governance' },
  { name: 'Other', value: 'Other' },
]

export const PRIVACY_OPTIONS: SelectItem[] = [
  { name: 'Public', value: 'public' },
  { name: 'Private', value: 'private' },
]

/**
 * Hook to determine if the current user can edit a playbook. A global playbook is
 * also editable only from the account that owns it.
 */
export const useCanEditPlaybook = (playbook?: Playbook | null): boolean => {
  const { data: currentUser } = useUser()
  const canUpdatePlaybooks = useCanPerformAction(
    CAPABILITY.CAN_UPDATE_PLAYBOOKS,
  )

  return useMemo(() => {
    if (!currentUser) {
      return false
    }
    if (!canUpdatePlaybooks) {
      return false
    }
    if (!playbook) {
      return true
    }
    if (!playbook.getIsGlobal()) {
      return true
    }
    // Global playbook - also check if user's account matches
    return playbook.getAccountId() === currentUser.getAccountid()
  }, [currentUser, playbook, canUpdatePlaybooks])
}

export const usePlaybooks = ({
  includeGlobalPlaybooks = true,
  categories = [],
}: {
  includeGlobalPlaybooks?: boolean
  categories?: string[]
}): GrpcResponse<PlaybooksResponse> => {
  const request = new ListPlaybooksRequest().setIncludeGlobalPlaybooks(
    includeGlobalPlaybooks,
  )
  if (categories.length > 0) {
    request.setCategoriesList(categories)
  }
  const { response } = useSwrGrpc(
    PlaybookServicePromiseClient.prototype.listPlaybooks,
    request,
  )
  return NewGrpcResponse(response)
}

// Fetch a single playbook by ID with full details (including prompts and parameters)
export const usePlaybook = (
  playbookId: string | null | undefined,
): GrpcResponse<PlaybookResponse> => {
  const request = useMemo(() => {
    return new ObjectIdRequest().setId(playbookId || '')
  }, [playbookId])

  const { response } = useSwrGrpc(
    PlaybookServicePromiseClient.prototype.getPlaybook,
    request,
    !!playbookId, // Only fetch when we have a playbook ID
  )
  return NewGrpcResponse(response)
}

// TODO: Test and verify RunPlaybook hook implementation
// This hook calls the RunPlaybook RPC to execute a playbook
export const useRunPlaybook = (): ((
  playbookId: string,
  optionalQuestion?: string,
  isVisibleInChat?: boolean,
) => Promise<RunPlaybookResponse | undefined>) => {
  const baseHeader = useAuthHeader()

  return useCallback(
    async (
      playbookId: string,
      optionalQuestion?: string,
      isVisibleInChat?: boolean,
    ) => {
      try {
        const playbookInputs = new PlaybookInputs().setPlaybookId(playbookId)
        const request = new RunPlaybookRequest()
          .setPlaybookInputs(playbookInputs)
          .setIsVisibleInChat(isVisibleInChat ?? true)
        if (optionalQuestion) {
          request.setOptionalQuestion(optionalQuestion)
        }

        const headers = {
          ...baseHeader,
          ...getRefererHeader(),
        }

        const response = await callGrpc(
          PlaybookServicePromiseClient.prototype.runPlaybook,
          request,
          headers,
        )
        return response
      } catch (error) {
        log.error('Error running playbook:', error)
        throw error
      }
    },
    [baseHeader],
  )
}

export const shouldGenerateEndSummaryForPlaybook = (
  playbook: Pick<Playbook, 'getGenerateSummaryReport'>,
): boolean => {
  return playbook.getGenerateSummaryReport()
}

// Prepare playbook inputs for launching (without creating a chat or sending a message)
// This is used when the chat already exists and we want to use the shared onSend callback
/**
 * A parameter the launcher already knows the answer to, so the user is not asked.
 *
 * An override means "this value is settled": the backend drops the parameter
 * from the ones needing input, so no picker is drawn for it at all. Use it only
 * where the answer follows from where the user launched from -- a guess the user
 * still has to confirm belongs in the picker, preselected.
 */
export type PlaybookParameterOverride = {
  promptName: string
  parameterName: string
  value: string
}

export const usePreparePlaybookLaunch = (): ((
  playbook: Playbook,
  overrides?: PlaybookParameterOverride[],
) => {
  playbookInputs: PlaybookInputs
  initiatePrompt: string
}) => {
  return useCallback(
    (playbook: Playbook, overrides?: PlaybookParameterOverride[]) => {
      const playbookInputs = new PlaybookInputs()
        .setPlaybookId(playbook.getId())
        .setGenerateEndSummary(shouldGenerateEndSummaryForPlaybook(playbook))

      if (overrides?.length) {
        // Grouped by prompt: the wire shape is one entry per prompt carrying its
        // parameters, not one entry per parameter.
        const byPrompt = new Map<string, PlaybookRunParameter[]>()
        overrides.forEach(({ promptName, parameterName, value }) => {
          const param = new PlaybookRunParameter()
            .setParameterName(parameterName)
            .setValuesList([value])
          byPrompt.set(promptName, [...(byPrompt.get(promptName) || []), param])
        })
        playbookInputs.setPromptParameterOverridesList(
          [...byPrompt.entries()].map(([promptName, params]) =>
            new PlaybookRunPromptParameter()
              .setPromptName(promptName)
              .setParametersList(params),
          ),
        )
      }

      return {
        playbookInputs,
        initiatePrompt: playbook.getInitiatePrompt(),
      }
    },
    [],
  )
}

// The Import Questionnaire playbook's Destination parameter, which decides
// whether the upload becomes a template to send to vendors or a questionnaire
// somebody sent US to answer.
//
// Tokens rather than the user-facing option labels ("Template — I will send
// this to my vendors"): the tool matches on the leading word and its own
// argument is documented as 'template' | 'received', so this cannot drift when
// that wording changes.
const IMPORT_QUESTIONNAIRE_PLAYBOOK = 'Import Questionnaire'
const IMPORT_DESTINATION_PARAM = 'Destination'
const IMPORT_DESTINATION_RECEIVED = 'received'
const IMPORT_DESTINATION_TEMPLATE = 'template'

/**
 * Answers the Destination question from where the user launched from, so the
 * common cases do not stop to ask.
 *
 * On the Customer Questionnaires page every import is one somebody sent us; on
 * Vendors Management every import is a template to send out. Anywhere else the
 * intent is genuinely unknown and the picker still asks -- unless the vendor
 * half is turned off, in which case a template is something this account cannot
 * send, and offering the choice would only let them pick a dead end.
 *
 * Returns the overrides to pass to preparePlaybookLaunch; empty means ask.
 */
export const useImportQuestionnaireDestination = (): ((
  playbook: Playbook,
  referringObjectType?: MODEL_TYPE,
) => PlaybookParameterOverride[]) => {
  const hasVendorTprmQuestionnaire = useHasVendorTprmQuestionnaire()

  return useCallback(
    (playbook: Playbook, referringObjectType?: MODEL_TYPE) => {
      if (playbook.getName() !== IMPORT_QUESTIONNAIRE_PLAYBOOK) return []

      let value: string | undefined
      if (referringObjectType === MODEL_TYPE.QUESTIONNAIRE) {
        value = IMPORT_DESTINATION_RECEIVED
      } else if (referringObjectType === MODEL_TYPE.VENDOR) {
        value = IMPORT_DESTINATION_TEMPLATE
      } else if (!hasVendorTprmQuestionnaire) {
        value = IMPORT_DESTINATION_RECEIVED
      }
      if (!value) return []

      return [
        {
          // The playbook's single prompt carries the playbook's own name.
          promptName: IMPORT_QUESTIONNAIRE_PLAYBOOK,
          parameterName: IMPORT_DESTINATION_PARAM,
          value,
        },
      ]
    },
    [hasVendorTprmQuestionnaire],
  )
}

// Launch a playbook by creating a new chat, setting title/lead prompt, and sending message with playbook inputs
// This is the fallback when PlaybooksUI doesn't have access to an existing chat
export const useLaunchPlaybook = (): ((
  playbook: Playbook,
) => Promise<void>) => {
  const createChat = useCreateChat()
  const updateChat = useUpdateChat()
  const sendChatMessage = useSendChatMessage()
  const navigateToChat = useNavigateToChat()
  const setPendingPromptForChat = useSetPendingPromptForChat()
  const preparePlaybookLaunch = usePreparePlaybookLaunch()

  return useCallback(
    async (playbook: Playbook) => {
      try {
        // 1. Create a new chat
        const { chat } = await createChat()

        // 2. Set chat title to playbook name
        chat.setName(playbook.getName())

        // 3. Set lead prompt to playbook's lead prompt, fallback to description if missing
        const leadPrompt = playbook.getLeadPrompt() || playbook.getDescription()
        chat.setLeadPrompt(leadPrompt)

        // 4. Prepare playbook inputs
        const { playbookInputs, initiatePrompt } =
          preparePlaybookLaunch(playbook)

        // 5. Check if playbook has an initiate prompt
        if (initiatePrompt) {
          // If playbook has initiate prompt, store playbook inputs on chat and navigate
          // The user will see the initiate prompt pre-filled and can send when ready
          chat.setInitiatePrompt(initiatePrompt)
          await updateChat(chat, playbookInputs)
          navigateToChat(chat.getId())
        } else {
          // No initiate prompt - update chat and send message immediately
          await updateChat(chat)
          await sendChatMessage({
            chat,
            prompt: '',
            playbookInputs,
            onSuccess: async (questionId: string, taskId: string) => {
              // Set pending prompt so the conversation panel will poll for task completion
              setPendingPromptForChat(chat.getId(), {
                taskId,
                prompt: '',
                questionId,
              })
              navigateToChat(chat.getId())
            },
            onError: (error) => {
              log.error('Error launching playbook:', error)
              toast.error('Failed to launch playbook')
            },
          })
        }
      } catch (error) {
        log.error('Error launching playbook:', error)
        throw error
      }
    },
    [
      createChat,
      updateChat,
      sendChatMessage,
      navigateToChat,
      setPendingPromptForChat,
      preparePlaybookLaunch,
    ],
  )
}

// TODO: Test and verify DeletePlaybook hook implementation
export const useDeletePlaybook = (): ((
  playbookId: string,
) => Promise<void>) => {
  const baseHeader = useAuthHeader()
  const { mutate } = useSWRConfig()

  return useCallback(
    async (playbookId: string) => {
      try {
        const request = new ObjectIdRequest().setId(playbookId)
        const headers = {
          ...baseHeader,
          ...getRefererHeader(),
        }

        await callGrpc(
          PlaybookServicePromiseClient.prototype.deletePlaybook,
          request,
          headers,
        )

        // Invalidate playbooks cache to refresh the list
        mutate((key) => {
          if (
            Array.isArray(key) &&
            key[0]?.toString().includes('listPlaybooks')
          ) {
            return true
          }
          return false
        })
      } catch (error) {
        log.error('Error deleting playbook:', error)
        throw error
      }
    },
    [baseHeader, mutate],
  )
}

// TODO: Test and verify SetFavorite hook implementation
export const useSetFavorite = (): ((
  playbookId: string,
  favorite: boolean,
) => Promise<void>) => {
  const baseHeader = useAuthHeader()
  const { mutate } = useSWRConfig()

  return useCallback(
    async (playbookId: string, favorite: boolean) => {
      try {
        const request = new FavoriteRequest()
          .setPlaybookId(playbookId)
          .setFavorite(favorite)
        const headers = {
          ...baseHeader,
          ...getRefererHeader(),
        }

        await callGrpc(
          PlaybookServicePromiseClient.prototype.setFavorite,
          request,
          headers,
        )

        // Invalidate playbooks cache to refresh the list
        mutate((key) => {
          if (
            Array.isArray(key) &&
            key[0]?.toString().includes('listPlaybooks')
          ) {
            return true
          }
          return false
        })
      } catch (error) {
        log.error('Error setting favorite:', error)
        throw error
      }
    },
    [baseHeader, mutate],
  )
}

// TODO: Test and verify MakePrivate hook implementation
export const useMakePrivate = (): ((
  playbookId: string,
  isPrivate: boolean,
) => Promise<void>) => {
  const baseHeader = useAuthHeader()
  const { mutate } = useSWRConfig()

  return useCallback(
    async (playbookId: string, isPrivate: boolean) => {
      try {
        const request = new MakePrivateRequest()
          .setPlaybookId(playbookId)
          .setIsPrivate(isPrivate)
        const headers = {
          ...baseHeader,
          ...getRefererHeader(),
        }

        await callGrpc(
          PlaybookServicePromiseClient.prototype.makePrivate,
          request,
          headers,
        )

        // Invalidate playbooks cache to refresh the list
        mutate((key) => {
          if (
            Array.isArray(key) &&
            key[0]?.toString().includes('listPlaybooks')
          ) {
            return true
          }
          return false
        })
      } catch (error) {
        log.error('Error making playbook private:', error)
        throw error
      }
    },
    [baseHeader, mutate],
  )
}

// Helper function to build PlaybookPrompt with parameters
// Used by both create and update playbook hooks
async function buildPlaybookPrompt(
  prompt: string,
  parameters: Parameter[] | undefined,
  uploadFiles: (files: File[]) => Promise<Map<string, string>>,
  includeExistingDocuments: boolean,
): Promise<PlaybookPrompt> {
  const playbookParameters: PlaybookParameter[] = []

  if (parameters && parameters.length > 0) {
    // First, upload all document files and get content IDs
    const documentFiles: File[] = []
    const parameterFileMap = new Map<Parameter, File[]>()

    parameters.forEach((param) => {
      if (param.type === 'document' && param.selectedDocuments.length > 0) {
        documentFiles.push(...param.selectedDocuments)
        parameterFileMap.set(param, param.selectedDocuments)
      }
    })

    // Upload all document files at once
    let contentIdsMap = new Map<string, string>()
    if (documentFiles.length > 0) {
      contentIdsMap = await uploadFiles(documentFiles)
    }

    // Build PlaybookParameters
    for (const param of parameters) {
      const playbookParam = new PlaybookParameter()
        .setName(param.name)
        .setDescription(param.description)
        .setValueSource(
          param.valueSource === 'ask_user'
            ? ValueSource.VALUE_SOURCE_ASK_USER
            : param.valueSource === 'optional'
            ? ValueSource.VALUE_SOURCE_OPTIONAL
            : ValueSource.VALUE_SOURCE_USE_DEFAULTS,
        )
        .setAllowMultipleValues(true)

      // Set ID if it exists (for updates) - IDs starting with 'param-' are client-generated for new params
      if (param.id && !param.id.startsWith('param-')) {
        playbookParam.setId(param.id)
      }

      // Map parameter type to MODEL_TYPE
      let modelType: MODEL_TYPE
      const values: string[] = []

      switch (param.type) {
        case 'policy': {
          modelType = MODEL_TYPE.POLICY
          values.push(...param.selectedPolicies.map((p) => p.id))
          break
        }
        case 'control': {
          modelType = MODEL_TYPE.CONTROL
          values.push(...param.selectedControls.map((c) => c.id))
          break
        }
        case 'vendor': {
          modelType = MODEL_TYPE.VENDOR
          values.push(...param.selectedVendors.map((v) => v.id))
          break
        }
        case 'evidence': {
          modelType = MODEL_TYPE.EVIDENCE
          values.push(...param.selectedEvidences.map((e) => e.id))
          break
        }
        case 'playbook': {
          modelType = MODEL_TYPE.PLAYBOOK
          values.push(...(param.selectedPlaybooks || []).map((p) => p.id))
          break
        }
        case 'document': {
          // For documents, use content IDs from uploaded files
          const paramFiles = parameterFileMap.get(param) || []
          for (const file of paramFiles) {
            const contentId = contentIdsMap.get(file.name)
            if (contentId) {
              values.push(contentId)
            }
          }
          // Include existing document content IDs for updates
          if (includeExistingDocuments && param.documentDisplayInfo) {
            values.push(...param.documentDisplayInfo.map((d) => d.contentId))
          }
          modelType = MODEL_TYPE.CHAT_FILE_UPLOAD
          break
        }
        case 'text': {
          modelType = MODEL_TYPE.TEXT_INPUT
          if (param.textValue !== undefined && param.textValue !== '') {
            values.push(param.textValue)
          }
          break
        }
        case 'boolean': {
          modelType = MODEL_TYPE.BOOLEAN_INPUT
          values.push(param.booleanValue ? 'true' : 'false')
          break
        }
        default:
          modelType = MODEL_TYPE.POLICY
      }

      playbookParam.setParameterType(modelType).setValuesList(values)
      if (param.type === 'boolean' || param.type === 'text') {
        playbookParam.setAllowMultipleValues(false)
      }
      playbookParameters.push(playbookParam)
    }
  }

  // Create PlaybookPrompt (with or without parameters)
  const playbookPrompt = new PlaybookPrompt()
    .setName('Main Prompt')
    .setPrompt(prompt)

  if (playbookParameters.length > 0) {
    playbookPrompt.setParametersList(playbookParameters)
  }

  return playbookPrompt
}

// Helper to invalidate playbook caches
async function invalidatePlaybookCaches(
  mutate: ReturnType<typeof useSWRConfig>['mutate'],
  includeGetPlaybook = false,
) {
  await mutate(
    (key) => {
      if (Array.isArray(key) && key[0]?.toString().includes('listPlaybooks')) {
        return true
      }
      if (
        includeGetPlaybook &&
        Array.isArray(key) &&
        key[0]?.toString().includes('getPlaybook')
      ) {
        return true
      }
      return false
    },
    undefined,
    { revalidate: true },
  )
}

export const useCreatePlaybook = (): ((
  name: string,
  description: string,
  category: string,
  contexts: PlaybookContext[],
  isPrivate: boolean,
  prompt?: string,
  parameters?: Parameter[],
  summaryInstructions?: string,
) => Promise<PlaybookResponse | undefined>) => {
  const baseHeader = useAuthHeader()
  const { mutate } = useSWRConfig()
  const uploadFiles = useUploadPlaybookFiles()

  return useCallback(
    async (
      name: string,
      description: string,
      category: string,
      contexts: PlaybookContext[],
      isPrivate: boolean,
      prompt?: string,
      parameters?: Parameter[],
      summaryInstructions?: string,
    ) => {
      try {
        const request = new CreatePlaybookRequest()
          .setName(name)
          .setDescription(description)
          .setCategory(category)
          .setContextsList(contexts)
          .setIsPrivate(isPrivate)

        if (summaryInstructions) {
          request.setSummaryInstructions(summaryInstructions)
        }

        if (prompt) {
          const playbookPrompt = await buildPlaybookPrompt(
            prompt,
            parameters,
            uploadFiles,
            false, // Don't include existing documents for create
          )
          request.setPromptsList([playbookPrompt])
        }

        const headers = {
          ...baseHeader,
          ...getRefererHeader(),
        }

        const response = await callGrpc(
          PlaybookServicePromiseClient.prototype.createPlaybook,
          request,
          headers,
        )

        await invalidatePlaybookCaches(mutate)
        return response
      } catch (error) {
        log.error('Error creating playbook:', error)
        throw error
      }
    },
    [baseHeader, mutate, uploadFiles],
  )
}

export const useUpdatePlaybook = (): ((
  playbookId: string,
  accountId: string,
  name: string,
  description: string,
  category: string,
  contexts: PlaybookContext[],
  isPrivate: boolean,
  prompt?: string,
  parameters?: Parameter[],
  summaryInstructions?: string,
) => Promise<PlaybookResponse | undefined>) => {
  const baseHeader = useAuthHeader()
  const { mutate } = useSWRConfig()
  const uploadFiles = useUploadPlaybookFiles()

  return useCallback(
    async (
      playbookId: string,
      accountId: string,
      name: string,
      description: string,
      category: string,
      contexts: PlaybookContext[],
      isPrivate: boolean,
      prompt?: string,
      parameters?: Parameter[],
      summaryInstructions?: string,
    ) => {
      try {
        const playbook = new Playbook()
          .setId(playbookId)
          .setAccountId(accountId)
          .setName(name)
          .setDescription(description)
          .setCategory(category)
          .setContextsList(contexts)
          .setIsPrivate(isPrivate)
          .setSummaryInstructions(summaryInstructions || '')

        if (prompt) {
          const playbookPrompt = await buildPlaybookPrompt(
            prompt,
            parameters,
            uploadFiles,
            true, // Include existing documents for update
          )
          playbook.setPromptsList([playbookPrompt])
        }

        const request = new UpdatePlaybookRequest().setPlaybook(playbook)

        const headers = {
          ...baseHeader,
          ...getRefererHeader(),
        }

        const response = await callGrpc(
          PlaybookServicePromiseClient.prototype.updatePlaybook,
          request,
          headers,
        )

        await invalidatePlaybookCaches(mutate, true) // Also invalidate getPlaybook cache
        return response
      } catch (error) {
        log.error('Error updating playbook:', error)
        throw error
      }
    },
    [baseHeader, mutate, uploadFiles],
  )
}

const useSearchablePlaybooks = (
  data: Playbook[] | undefined,
): SearchableData<Playbook>[] => {
  return useMemo(() => {
    if (!data) {
      return []
    }

    return prepareSearchableData(
      data,
      (playbook) => playbook.getId(),
      (playbook) => {
        const name = playbook.getName() || ''
        const description = playbook.getDescription() || ''
        const category = playbook.getCategory() || ''

        return [name, description, category].filter((value) => !!value)
      },
    )
  }, [data])
}

export const usePlaybooksFilterAndSearchData = (
  playbooks: Playbook[],
): SearchableData<Playbook>[] => {
  const { setValues } = useFilterBarValues()

  const filterData = useMemo((): {
    playbookCategories: FilterValue[]
  } => {
    if (!playbooks.length) {
      return {
        playbookCategories: [],
      }
    }

    const categorySet = new Set<string>()

    playbooks.forEach((playbook) => {
      const category = playbook.getCategory()
      if (category) {
        categorySet.add(category)
      }
    })

    return {
      playbookCategories: Array.from(categorySet).sort(),
    }
  }, [playbooks])

  // Set playbook filter values in the context when they change
  useEffect(() => {
    setValues('playbookCategories', filterData.playbookCategories)
  }, [filterData, setValues])

  return useSearchablePlaybooks(playbooks)
}

const filterConfig: Filter[] = [
  {
    key: FilterParam.PLAYBOOK_TYPE,
    property: 'playbookType',
    // Note: Even though it's single-select in the UI, the transform returns an array
    // and the filter logic converts the single string value to an array for comparison
    type: 'array',
  },
  {
    key: FilterParam.PLAYBOOK_CATEGORY,
    property: 'category',
  },
]

const createTransform = (currentUserId: string): Transform<Playbook> => {
  return (playbook: Playbook) => {
    // Build array of playbook types this playbook matches
    const types: string[] = []

    // Determine base playbook type
    if (playbook.getIsGettingStarted()) {
      types.push('getting_started')
    }
    if (playbook.getIsGlobal()) {
      types.push('trustero_built_in')
    } else {
      types.push('custom')
    }

    // Check if it's a favorite
    if (playbook.getIsFavorite()) {
      types.push('favorite')
    }

    // Check if it's my playbook
    const createdBy = playbook.getCreatedBy()
    if (createdBy && createdBy.toString() === currentUserId) {
      types.push('my_playbooks')
    }

    // Always include 'all' so it shows up when "All" filter is selected
    types.push('all')

    return {
      playbookType: types,
      category: playbook.getCategory() || '',
    }
  }
}

export const useFilteredPlaybooks = (
  playbooks: Playbook[],
  currentUserId: string,
): Playbook[] => {
  const transform = useMemo(
    () => createTransform(currentUserId),
    [currentUserId],
  )

  return useFilters({
    filters: filterConfig,
    data: playbooks,
    transform,
  })
}

// Hook to upload files for playbooks
// Returns a map of file name to content id
export const useUploadPlaybookFiles = (): ((
  files: File[],
) => Promise<Map<string, string>>) => {
  const baseHeader = useAuthHeader()

  return useCallback(
    async (files: File[]): Promise<Map<string, string>> => {
      try {
        // Convert files to FileUploadBody objects
        const fileUploads = await Promise.all(
          files.map(async (file) => {
            const fileBytes = await getBytesPayload(file)
            return new FileUploadBody()
              .setFileName(file.name)
              .setMimeType(file.type || 'application/octet-stream')
              .setFile(fileBytes)
              .setCaption(file.name)
          }),
        )

        const request = new UploadFilesRequest().setFileUploadsList(fileUploads)

        const headers = {
          ...baseHeader,
          ...getRefererHeader(),
        }

        const response = await callGrpc(
          PlaybookServicePromiseClient.prototype.uploadFiles,
          request,
          headers,
        )

        // Convert the map from proto to JavaScript Map
        const contentIdsMap = response.getContentIdsMap()
        const result = new Map<string, string>()
        if (contentIdsMap) {
          contentIdsMap.forEach((contentId, fileName) => {
            result.set(fileName, contentId)
          })
        }

        return result
      } catch (error) {
        log.error('Error uploading playbook files:', error)
        throw error
      }
    },
    [baseHeader],
  )
}

// Hook to fetch chats for a playbook
export const useChatsForPlaybook = (
  playbookId: string | null | undefined,
): GrpcResponse<ListChatsForPlaybookResponse> => {
  const request = useMemo(() => {
    return new ListChatsForPlaybookRequest().setPlaybookId(playbookId || '')
  }, [playbookId])

  const { response } = useSwrGrpc(
    ChatServicePromiseClient.prototype.listChatsForPlaybook,
    request,
    !!playbookId, // Only fetch when we have a playbook ID
  )
  return NewGrpcResponse(response)
}

export const useDownloadPlaybook = (
  playbook: Playbook | null | undefined,
): (() => void) => {
  return useCallback(() => {
    if (!playbook) return
    const data = JSON.stringify(
      { type: 'playbook', playbook: playbook.toObject() },
      null,
      2,
    )
    const blob = new Blob([data], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${playbook.getName() || 'playbook'}.json`
    a.click()
    URL.revokeObjectURL(url)
  }, [playbook])
}

type LookupMaps = {
  policyMap: Map<string, string>
  controlMap: Map<string, { name: string; modelId: string }>
  vendorMap: Map<string, string>
  evidenceMap: Map<string, { name: string; isBroken: boolean }>
  playbookMap?: Map<string, string>
}

type FormSetters = {
  setName: (v: string) => void
  setDescription: (v: string) => void
  setPrompt: (v: string) => void
  setSelectedCategory: (v: SelectItem[]) => void
  setSelectedPrivacy: (v: SelectItem[]) => void
  setSelectedContexts: (v: string[]) => void
  setParameters: (v: Parameter[]) => void
  setSummaryInstructions: (v: string) => void
  setShowError: (v: boolean) => void
  setBusy: (v: boolean) => void
}

export const usePopulateFormFromJson = (
  maps: LookupMaps,
  setters: FormSetters,
): ((pb: Partial<Playbook.AsObject>) => void) => {
  const { policyMap, controlMap, vendorMap, evidenceMap, playbookMap } = maps
  const {
    setName,
    setDescription,
    setPrompt,
    setSelectedCategory,
    setSelectedPrivacy,
    setSelectedContexts,
    setParameters,
    setSummaryInstructions,
    setShowError,
    setBusy,
  } = setters

  return useCallback(
    (pb: Partial<Playbook.AsObject>) => {
      setName(pb.name || '')
      setDescription(pb.description || '')

      const promptsList = pb.promptsList || []
      if (promptsList.length > 0) {
        const firstPrompt = promptsList[0]
        setPrompt(firstPrompt.prompt || '')

        const paramsList = firstPrompt.parametersList || []
        if (paramsList.length > 0) {
          const loadedParameters: Parameter[] = paramsList.map(
            (param, index: number) => {
              const modelType = param.parameterType
              let type: ParameterType = 'policy'
              if (modelType === MODEL_TYPE.CONTROL) type = 'control'
              else if (modelType === MODEL_TYPE.VENDOR) type = 'vendor'
              else if (modelType === MODEL_TYPE.CHAT_FILE_UPLOAD)
                type = 'document'
              else if (modelType === MODEL_TYPE.EVIDENCE) type = 'evidence'
              else if (modelType === MODEL_TYPE.TEXT_INPUT) type = 'text'
              else if (modelType === MODEL_TYPE.BOOLEAN_INPUT) type = 'boolean'
              else if (modelType === MODEL_TYPE.PLAYBOOK) type = 'playbook'

              const vs = param.valueSource
              const valueSource =
                vs === ValueSource.VALUE_SOURCE_ASK_USER
                  ? 'ask_user'
                  : vs === ValueSource.VALUE_SOURCE_OPTIONAL
                  ? 'optional'
                  : 'defaults'

              const values: string[] = param.valuesList || []

              return {
                id: param.id || `param-new-${index}-${param.name}`,
                name: param.name || '',
                description: param.description || '',
                type,
                valueSource,
                selectedPolicies:
                  type === 'policy'
                    ? values.map((id) => ({
                        id,
                        name: policyMap.get(id) || id,
                      }))
                    : [],
                selectedControls:
                  type === 'control'
                    ? values.map((id) => {
                        const control = controlMap.get(id)
                        return {
                          id,
                          modelId: control?.modelId || id,
                          name: control?.name || id,
                        }
                      })
                    : [],
                selectedVendors:
                  type === 'vendor'
                    ? values.map((id) => ({
                        id,
                        name: vendorMap.get(id) || id,
                      }))
                    : [],
                selectedEvidences:
                  type === 'evidence'
                    ? values.map((id): SelectedEvidence => {
                        const ev = evidenceMap.get(id)
                        return {
                          id,
                          name: ev?.name || id,
                          isBroken: ev?.isBroken ?? false,
                        }
                      })
                    : [],
                selectedPlaybooks:
                  type === 'playbook'
                    ? values.map(
                        (id): SelectedPlaybook => ({
                          id,
                          name: playbookMap?.get(id) || id,
                        }),
                      )
                    : [],
                selectedDocuments: [],
                documentDisplayInfo: [],
                textValue: type === 'text' ? values[0] ?? '' : '',
                booleanValue: type === 'boolean' ? values[0] === 'true' : false,
              }
            },
          )
          setParameters(loadedParameters)
        } else {
          setParameters([])
        }
      } else {
        setPrompt('')
        setParameters([])
      }

      const categoryOption = CATEGORY_OPTIONS.find(
        (c) => c.value === (pb.category || ''),
      )
      setSelectedCategory(categoryOption ? [categoryOption] : [])

      setSelectedPrivacy([
        pb.isPrivate ? PRIVACY_OPTIONS[1] : PRIVACY_OPTIONS[0],
      ])

      const contextsList = pb.contextsList || []
      if (contextsList.length > 0) {
        setSelectedContexts(
          contextsList
            .filter(
              (context) =>
                context.dataSource !==
                ChatContextSourceType.SOC2_REPORT_SUMMARY,
            )
            .map((context) => context.dataSource.toString()),
        )
      }

      setSummaryInstructions(pb.summaryInstructions || '')
      setShowError(false)
      setBusy(false)
    },
    [
      policyMap,
      controlMap,
      vendorMap,
      evidenceMap,
      playbookMap,
      setName,
      setDescription,
      setPrompt,
      setSelectedCategory,
      setSelectedPrivacy,
      setSelectedContexts,
      setParameters,
      setSummaryInstructions,
      setShowError,
      setBusy,
    ],
  )
}

/**
 * Whether this account may see a given playbook, per its `feature_flag`.
 *
 * ListPlaybooks returns every playbook and never filters on the flag itself --
 * it only carries the value on the wire -- so each surface that OFFERS a
 * playbook has to apply this. Surfaces that merely look one up (resolving a
 * report's playbook name, checking a new playbook's name is unique) must NOT:
 * hiding a row there produces a blank name or lets a user create a duplicate
 * colliding with a playbook they cannot see.
 *
 * An unrecognised flag hides the playbook. It used to show it, which meant a
 * typo in a migration's feature_flag string silently revealed a gated playbook
 * to everyone, with nothing failing. Erring the other way makes that mistake
 * visible -- the playbook goes missing -- instead of invisible.
 */
export const useIsPlaybookVisible = (): ((playbook: Playbook) => boolean) => {
  const resolvedFeatureFlags = useResolvedPlaybookFeatureFlags()

  return useCallback(
    (playbook: Playbook) => {
      const flag = playbook.getFeatureFlag()
      if (!flag) return true
      return resolvedFeatureFlags[flag] === true
    },
    [resolvedFeatureFlags],
  )
}
